import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { isEqual } from 'lodash';
import { Link } from 'react-router-dom';
import Actions, { thunks } from '@redux/app';
import config from '@config';
import { clearReduxStoreAndLocalStorage } from '@config/utils';
import { withMsal } from '@azure/msal-react';
import { SplashScreen } from '@components';
import utils from '@utils';
import Form from './components/Form';
import { ADOPERATIONS, ROUTES } from '../../utils/constants';
import './login.scss';

class Login extends Component {
	constructor(props) {
		super(props);
		this.state = {
			prepayUser            : '',
			payEstimate           : false,
			prepayUserLoggingIn   : false,
			updatePaymentRedirect : false,
			reviewRequest         : false,
			jobRequest            : false,
			reviewJobId           : null,
			reviewOfficerId       : null,
		};
	}

	componentDidMount() {
		// Initially clears the redux store and local storage
		clearReduxStoreAndLocalStorage();
		const { location } = this.props;
		/**
		 * RKP-1453: Sometimes, location is undefined either in IE11 or in Incognito browsing
		 * To make sure that there are no crashes due to null or undefined references, we're checking
		 * multiple conditions so that no variables are read unless they're defined
		*/
		let from;
		let search;
		if (location) ({ search } = location);
		if (location && location.state) ({ state : { from } } = location);

		if (search) {
			const params = new URLSearchParams(search);
			const action = params.get('action');
			const newState = { ...this.state };

			if (action && action === 'review') {
				newState.reviewRequest = true;
				newState.reviewJobId = params.get('jobId');
				newState.reviewOfficerId = params.get('officerId');
			} else if (action && action === 'request') {
				newState.jobRequest = true;
			}

			const prepayUser = params.get('user');
			newState.payEstimate = params.get('estId') !== null;

			if (prepayUser) {
				newState.prepayUser = decodeURIComponent(prepayUser);
			}

			this.setState(newState);
		} else if (from && from.search === '?updatePayment') {
			this.setState({ updatePaymentRedirect : true });
		}
	}

	shouldComponentUpdate = ({ isAuthenticated, isProcessingMsal }, { prepayUser }) => {
		const lastProps = this.props;
		const lastState = this.state;

		return (
			!isEqual(isAuthenticated, lastProps.isAuthenticated)
			|| !isEqual(prepayUser, lastState.prepayUser)
			|| !isEqual(isProcessingMsal, lastProps.isProcessingMsal)
		);
	}

	componentDidUpdate({ isAuthenticated : wasAuthenticated }) {
		const {
			isAuthenticated,
			location : {
				state : currentLocationState,
				search,
			},
			history : { push },
		} = this.props;

		const {
			prepayUserLoggingIn,
			payEstimate,
			updatePaymentRedirect,
			reviewRequest,
			reviewJobId,
			reviewOfficerId,
			jobRequest,
		} = this.state;

		const { from } = currentLocationState || { from : { pathname : '/' } };
		if (!wasAuthenticated && isAuthenticated && !jobRequest && (prepayUserLoggingIn || payEstimate)) {
			const params = new URLSearchParams(search);
			const estId = params.get('estId');
			push(`/invoiceEstimate/1/${estId}`, { fromLogin : true });
		} else if (!wasAuthenticated && isAuthenticated && !jobRequest && updatePaymentRedirect) {
			push('/account#payment');
		} else if (!wasAuthenticated && isAuthenticated && !jobRequest) {
			if (reviewRequest) localStorage.setItem('review', `{"JobId":"${reviewJobId}","OfficerId":"${reviewOfficerId}"}`);
			push(from.pathname);
		}
	}

	login = ({ username, password }) => {
		const {
			login, history : { push },
		} = this.props;
		const { prepayUser } = this.state;
		this.setState({ prepayUserLoggingIn : prepayUser === username }, () => {
			if (window.adb2cEnabled) {
				this.ADLogin({ username });
			} else {
				login({ username, password }, () => {
					const { jobRequest, prepayUserLoggingIn } = this.state;
					// If coming from a job request and the username matches the one provided from the request form, redirect back there.
					if (jobRequest && prepayUserLoggingIn) push('/requestjob');
				});
			}
		});
	}

	ADLogin = ({ username : email }) => {
		const {
			msalContext: { instance: msalInstance },
		} = this.props;

		msalInstance.loginRedirect({
			loginHint   : email,
			authority   : config.ENV_OPTIONS.adb2c.policies.signin,
			scopes      : config.ENV_OPTIONS.adb2c.scopes,
			redirectUri : ROUTES.ADCallback,
			prompt     	: 'login',
			state       : utils.encodeADState({ operation : ADOPERATIONS.login }),
		});
	}

	render() {
		const { prepayUser } = this.state;
		const { isProcessingMsal } = this.props;
		const { adb2cEnabled } = window;

		if (isProcessingMsal) {
			return (<SplashScreen />);
		}

		return (
			<div id="login-container">
				<div className="container">
					<div className="row">
						<div id="img-wrapper" className="col-sm-6 col-sm-offset-3">
							<img
								role="presentation"
								src="/images/logo-blue.svg"
								alt="RollKall Logo"
							/>
						</div>
					</div>
					<div className="row">
						<div className="col-sm-6 col-sm-offset-3">
							<div id="login-panel">
								<Form onSubmit={this.login} username={prepayUser} />
							</div>
						</div>
					</div>
					<div className="row">
						<div className="col-sm-6 col-sm-offset-3">
							<div id="login-panel-footer">
								<span className="footer-info pull-left">
									v
									{config.ApplicationVersion}
								</span>
								<If condition={!adb2cEnabled}>
									<Link
										to="/resetPassword"
										className="btn btn-link padding-0 pull-right"
									>
										Forgot Password?
									</Link>
								</If>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}
}

const mapStateToProps = ({
	app : {
		isAuthenticated,
		isProcessingMsal,
		loginData,
	},
}) => ({
	...loginData,
	isAuthenticated,
	isProcessingMsal,
});

const mapDispatchToProps = (dispatch) => ({
	setCredentials : bindActionCreators(Actions, dispatch).setCredentials,
	login          : bindActionCreators(thunks, dispatch).login,
});

Login.propTypes = {
	login            : PropTypes.func.isRequired,
	isAuthenticated  : PropTypes.bool,
	location         : PropTypes.object,
	history          : PropTypes.object,
	msalContext      : PropTypes.object.isRequired,
	isProcessingMsal : PropTypes.bool,
};

Login.defaultProps = {
	isAuthenticated  : false,
	location         : {},
	history          : {},
	isProcessingMsal : false,
};
export default withMsal(connect(mapStateToProps, mapDispatchToProps)(Login));
